/**
 * AgentModal
 *
 * Opens when the user clicks an agent pill in the AgentBar.
 * Shows what the agent can do and provides a primary action.
 *
 * Pattern B (Headless UI Dialog) — see design guide modals-pattern-b.md
 */

import { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { X, Clock, Loader2, AlertCircle } from 'lucide-react';
import { AgentGlyph } from './AgentGlyph';
import type { AgentDescriptor } from './types';

const GRADIENT_HEADER: Record<string, string> = {
  amber:   'from-amber-500/10 to-orange-500/5',
  indigo:  'from-indigo-500/10 to-violet-500/5',
  emerald: 'from-emerald-500/10 to-teal-500/5',
  rose:    'from-rose-500/10 to-pink-500/5',
};

const RUN_BTN: Record<string, string> = {
  amber:   'bg-[var(--mustard)] hover:bg-[var(--mustard)]',
  indigo:  'bg-[var(--slate)] hover:bg-[var(--slate)]',
  emerald: 'bg-[var(--green)] hover:bg-[var(--green)]',
  rose:    'bg-[var(--coral)] hover:bg-[var(--coral)]',
};

const CAPABILITY_DOT: Record<string, string> = {
  amber:   'bg-[var(--mustard)]',
  indigo:  'bg-[var(--slate)]',
  emerald: 'bg-[var(--green)]',
  rose:    'bg-[var(--coral)]',
};

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(iso).toLocaleDateString();
}

interface AgentModalProps {
  agent: AgentDescriptor;
  onClose: () => void;
}

export function AgentModal({ agent, onClose }: AgentModalProps) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const headerGradient = GRADIENT_HEADER[agent.colorScheme] ?? GRADIENT_HEADER.amber;
  const runBtnClass    = RUN_BTN[agent.colorScheme]         ?? RUN_BTN.amber;
  const capDotClass    = CAPABILITY_DOT[agent.colorScheme]  ?? CAPABILITY_DOT.amber;

  async function handleRun() {
    setError(null);
    setRunning(true);
    try {
      await agent.onRun();
      // Close immediately — the result is already written to the page (store updated
      // synchronously before this line runs), so the user sees it right away.
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      setRunning(false);
    }
  }

  const isRunning = running || agent.isRunning;
  const btnDisabled = isRunning || agent.disabled;

  return (
    <Transition appear show as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"     enterFrom="opacity-0"     enterTo="opacity-100"
          leave="ease-in duration-150"      leaveFrom="opacity-100"   leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Panel */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-2 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 translate-y-2 scale-95"
          >
            <Dialog.Panel className="relative w-full max-w-sm bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 overflow-hidden">

              {/* Header */}
              <div className={`px-5 pt-5 pb-4 bg-gradient-to-br ${headerGradient}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <AgentGlyph
                      type={agent.glyphType}
                      colorScheme={agent.colorScheme}
                      size={44}
                      pulse={!isRunning}
                    />
                    <div>
                      <Dialog.Title className="text-base font-semibold text-[var(--text-pri)] leading-tight">
                        {agent.name}
                      </Dialog.Title>
                      <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">
                        {agent.tagline}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-shrink-0 p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:bg-[var(--bg-raised)]/60 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="px-5 py-4 space-y-4">
                {/* Description */}
                <p className="text-sm text-[var(--text-sec)] leading-relaxed">
                  {agent.description}
                </p>

                {/* Capabilities */}
                {agent.capabilities.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-2">
                      What it does
                    </p>
                    <ul className="space-y-2">
                      {agent.capabilities.map((cap) => (
                        <li key={cap.label} className="flex items-start gap-2.5">
                          <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${capDotClass}`} />
                          <div>
                            <span className="text-xs font-medium text-[var(--text-pri)]">{cap.label}</span>
                            {cap.description && (
                              <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">{cap.description}</p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Error */}
                {error && (
                  <div className="flex items-start gap-2 text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] px-3 py-2">
                    <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                    {error}
                  </div>
                )}


              </div>

              {/* Footer */}
              <div className="px-5 pb-5 flex items-center justify-between gap-3">
                {/* Last run info */}
                <div className="text-xs text-[var(--text-ter)] flex items-center gap-1.5">
                  {agent.lastRunAt ? (
                    <>
                      <Clock className="h-3 w-3 flex-shrink-0" />
                      Last run {formatRelative(agent.lastRunAt)}
                    </>
                  ) : (
                    <span className="text-[var(--text-ter)]">Never run</span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-3 py-1.5 text-xs font-medium border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleRun}
                    disabled={btnDisabled}
                    title={agent.disabled ? agent.disabledReason : undefined}
                    className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium  text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${runBtnClass}`}
                  >
                    {isRunning && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {isRunning ? 'Running…' : (agent.runLabel ?? 'Run agent')}
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
